/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: OpenVPNClientPage.tsx
 🗓️ Created: 2026-05-02
 🧩 Description:
    Implementation of the openvpn-client module.
────────────────────────────────────────────────────────────────*/


import React, { useState, useCallback, useRef } from 'react';
import { Link, Upload, Wifi, WifiOff, RefreshCw, Trash2, Play, Pause, Server, ArrowUpDown, Clock } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useOpenVPNClient } from './hooks/useOpenVPNClient';
import { ModuleFormHeader } from '../../components/layouts';
import { FormBox } from '../../components/ui/FormBox';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import ConfirmationModal from '../../components/ui/ConfirmationModal';

function formatBytes(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

function formatDate(dateStr?: string): string {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
}

export default function OpenVPNClientPage() {
    const { t } = useI18n();
    const {
        connection,
        loading,
        uploading,
        connecting,
        uploadConfig,
        connect,
        disconnect,
        deleteConfiguration,
        refreshStatus
    } = useOpenVPNClient();

    const fileInputRef = useRef<HTMLInputElement>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = async (event) => {
                const content = event.target?.result as string;
                await uploadConfig(content);
            };
            reader.readAsText(file);
        }
    }, [uploadConfig]);

    const handleUploadClick = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    const handleConnect = useCallback(async () => {
        await connect();
    }, [connect]);

    const handleDisconnect = useCallback(async () => {
        await disconnect();
    }, [disconnect]);

    const handleDeleteClick = useCallback(() => {
        setShowDeleteModal(true);
    }, []);

    const confirmDelete = useCallback(async () => {
        setShowDeleteModal(false);
        await deleteConfiguration();
    }, [deleteConfiguration]);

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'connected': return 'text-green-500';
            case 'connecting': return 'text-yellow-500';
            case 'error': return 'text-red-500';
            default: return 'text-muted-foreground';
        }
    };

    const getStatusBg = (status: string) => {
        switch (status) {
            case 'connected': return 'bg-green-500/10 border-green-500/30';
            case 'connecting': return 'bg-yellow-500/10 border-yellow-500/30';
            case 'error': return 'bg-red-500/10 border-red-500/30';
            default: return 'bg-muted/50 border-muted';
        }
    };

    const getStatusIcon = (status: string, size: string = 'h-5 w-5') => {
        switch (status) {
            case 'connected': return <Wifi className={`${size} text-green-500`} />;
            case 'connecting': return <RefreshCw className={`${size} text-yellow-500 animate-spin`} />;
            default: return <WifiOff className={`${size} text-muted-foreground`} />;
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <div className="p-6 pb-20">
            {/* Hidden file input */}
            <input
                ref={fileInputRef}
                type="file"
                accept=".ovpn,.conf"
                onChange={handleFileChange}
                className="hidden"
            />

            {/* Header with actions on the right */}
            <ModuleFormHeader
                icon={<Link className="h-5 w-5 text-primary" />}
                title={t('openvpnClient.title', 'OpenVPN Client')}
                backButtonText=""
                onBack={() => { }}
                hideBackButton={true}
            >
                <div className="flex items-center gap-2">
                    {connection?.hasConfig ? (
                        <>
                            {connection.status === 'connected' ? (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleDisconnect}
                                    disabled={connecting}
                                >
                                    <Pause className="h-4 w-4 mr-2" />
                                    {t('openvpnClient.actions.disconnect', 'Disconnect')}
                                </Button>
                            ) : (
                                <Button
                                    variant="default"
                                    size="sm"
                                    onClick={handleConnect}
                                    disabled={connecting}
                                >
                                    <Play className="h-4 w-4 mr-2" />
                                    {t('openvpnClient.actions.connect', 'Connect')}
                                </Button>
                            )}
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={refreshStatus}
                                title={t('openvpnClient.actions.refresh', 'Refresh Status')}
                            >
                                <RefreshCw className="h-4 w-4" />
                            </Button>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleDeleteClick}
                                className="text-destructive hover:text-destructive"
                                title={t('openvpnClient.actions.delete', 'Delete Configuration')}
                            >
                                <Trash2 className="h-4 w-4" />
                            </Button>
                        </>
                    ) : (
                        <Button
                            variant="default"
                            size="sm"
                            onClick={handleUploadClick}
                            disabled={uploading}
                        >
                            {uploading ? (
                                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                                <Upload className="h-4 w-4 mr-2" />
                            )}
                            {t('openvpnClient.actions.upload', 'Upload Configuration')}
                        </Button>
                    )}
                </div>
            </ModuleFormHeader>

            {/* Main Content */}
            <div className="mt-8">
                {connection?.hasConfig ? (
                    <div className="space-y-4">
                        {/* Connection Status */}
                        <FormBox
                            title={t('openvpnClient.sections.status', 'Connection Status')}
                            icon={<Wifi className="h-5 w-5" />}
                        >
                            {/* Status Banner */}
                            <div className={`p-3 rounded-lg border mb-4 ${getStatusBg(connection.status)}`}>
                                <div className="flex items-center gap-3">
                                    {getStatusIcon(connection.status, 'h-6 w-6')}
                                    <div>
                                        <span className={`font-medium ${getStatusColor(connection.status)}`}>
                                            {t(`openvpnClient.status.${connection.status}`, connection.status)}
                                        </span>
                                        {connection.statusMessage && (
                                            <span className="text-sm text-muted-foreground ml-2">
                                                — {connection.statusMessage}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Connection Details - 4 column grid like Extensions */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">
                                {/* Row 1: Remote Host | Value | Remote Port | Value */}
                                <Label className="text-left">
                                    {t('openvpnClient.fields.remoteHost', 'Remote Host')}
                                </Label>
                                <div className="font-medium">
                                    {connection.remoteHost || '-'}
                                </div>
                                <Label className="text-left">
                                    {t('openvpnClient.fields.remotePort', 'Remote Port')}
                                </Label>
                                <div className="font-medium">
                                    {connection.remotePort || '-'}
                                </div>

                                {/* Row 2: Local IP | Value | Remote IP | Value */}
                                <Label className="text-left">
                                    {t('openvpnClient.fields.localIp', 'Local IP')}
                                </Label>
                                <div className="font-medium">
                                    {connection.localIp || '-'}
                                </div>
                                <Label className="text-left">
                                    {t('openvpnClient.fields.remoteIp', 'Remote IP')}
                                </Label>
                                <div className="font-medium">
                                    {connection.remoteIp || '-'}
                                </div>

                                {/* Row 3: Bytes Received | Value | Bytes Sent | Value */}
                                <Label className="text-left">
                                    {t('openvpnClient.fields.bytesReceived', 'Bytes Received')}
                                </Label>
                                <div className="font-medium text-green-500">
                                    ↓ {formatBytes(connection.bytesReceived)}
                                </div>
                                <Label className="text-left">
                                    {t('openvpnClient.fields.bytesSent', 'Bytes Sent')}
                                </Label>
                                <div className="font-medium text-blue-500">
                                    ↑ {formatBytes(connection.bytesSent)}
                                </div>

                                {/* Row 4: Connected Since | Value (span 3) */}
                                <Label className="text-left">
                                    {t('openvpnClient.fields.connectedSince', 'Connected Since')}
                                </Label>
                                <div className="font-medium col-span-3">
                                    {formatDate(connection.connectedSince)}
                                </div>
                            </div>
                        </FormBox>

                        {/* Configuration Info */}
                        <FormBox
                            title={t('openvpnClient.sections.configuration', 'Configuration')}
                            icon={<Server className="h-5 w-5" />}
                        >
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">
                                <Label className="text-left">
                                    {t('openvpnClient.fields.name', 'Connection Name')}
                                </Label>
                                <div className="font-medium col-span-3">
                                    {connection.name || 'Site-to-Site VPN'}
                                </div>

                                <Label className="text-left">
                                    {t('openvpnClient.fields.configStatus', 'Configuration')}
                                </Label>
                                <div className="font-medium col-span-3 flex items-center gap-2">
                                    <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-green-500/10 text-green-500 border border-green-500/30">
                                        ✓ {t('openvpnClient.fields.configLoaded', 'Loaded')}
                                    </span>
                                </div>
                            </div>
                        </FormBox>
                    </div>
                ) : (
                    /* No Configuration - Upload Prompt */
                    <FormBox
                        title={t('openvpnClient.sections.configuration', 'Configuration')}
                        icon={<Server className="h-5 w-5" />}
                    >
                        <div className="flex flex-col items-center justify-center py-12">
                            <div className="p-4 rounded-full bg-muted/50 mb-4">
                                <WifiOff className="h-12 w-12 text-muted-foreground/50" />
                            </div>
                            <h3 className="text-lg font-medium mb-2">
                                {t('openvpnClient.messages.noConfig', 'No configuration uploaded')}
                            </h3>
                            <p className="text-sm text-muted-foreground text-center max-w-md mb-6">
                                {t('openvpnClient.messages.noConfigDesc', 'Upload an .ovpn file to configure the site-to-site VPN connection')}
                            </p>
                            <Button
                                variant="default"
                                onClick={handleUploadClick}
                                disabled={uploading}
                            >
                                {uploading ? (
                                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                ) : (
                                    <Upload className="h-4 w-4 mr-2" />
                                )}
                                {t('openvpnClient.actions.upload', 'Upload Configuration')}
                            </Button>
                        </div>
                    </FormBox>
                )}
            </div>

            <ConfirmationModal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={confirmDelete}
                title={t('common.confirmDelete', 'Confirm Deletion')}
                message={t('openvpnClient.confirmDelete.message', 'Are you sure you want to delete the VPN configuration?')}
                confirmText={t('common.delete', 'Delete')}
                cancelText={t('common.cancel', 'Cancel')}
                type="danger"
            />
        </div>
    );
}
